import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Mail, Search } from "lucide-react";
import { getMemberBills, accountsBillPdfUrl, emailMemberBill } from "@/lib/api";
import { CsvButton, Empty, SortTh, Stat, TH, TR, downloadCsv, errMsg, money, useSort } from "@/components/accounts/shared";

// Every bill issued to members in the month, across gyms: one bill per payment, emailed
// automatically. Open the PDF, or email a bill again when it didn't reach the member.
const COLS = {
  issued_on: (r) => r.issued_on || "", number: (r) => r.number || "", gym: (r) => r.gym || "",
  member: (r) => (r.member || "").toLowerCase(), total: (r) => Number(r.total) || 0,
};

const SELECT = "bg-[#0a0a0a] border border-[#262626] rounded-md h-8 px-2 text-sm text-zinc-200";
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// The date range a period stands for: {from, to} (YYYY-MM-DD), or {} for all time.
function rangeFor(period, month, from, to) {
  const now = new Date();
  if (period === "month" && month) {
    const [y, m] = month.split("-").map(Number);
    return { from: `${month}-01`, to: iso(new Date(y, m, 0)) };
  }
  if (period === "last_month") return { from: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), to: iso(new Date(now.getFullYear(), now.getMonth(), 0)) };
  if (period === "fy") {
    const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;   // Indian financial year, April to March
    return { from: `${y}-04-01`, to: `${y + 1}-03-31` };
  }
  if (period === "range") return { from: from || undefined, to: to || undefined };
  return {};
}

const EMAIL_STATES = { emailed: (r) => !!r.emailed_to, failed: (r) => !r.emailed_to && !!r.email_error, not_sent: (r) => !r.emailed_to && !r.email_error, problem: (r) => !r.emailed_to };
const KINDS = { full: (r) => r.label === "Paid in full", part: (r) => (r.label || "").startsWith("Part payment"), final: (r) => r.label === "Final payment", old: (r) => !r.label };

export default function MemberBills({ month: headerMonth, country: headerCountry, gym: headerGym, gyms = [] }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(null);
  const [period, setPeriod] = useState("month");
  const [month, setMonth] = useState(headerMonth);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [country, setCountry] = useState(headerCountry || "");
  const [gym, setGym] = useState(headerGym || "");
  const [mode, setMode] = useState("");
  const [kind, setKind] = useState("");
  const [email, setEmail] = useState("");

  const range = rangeFor(period, month, from, to);
  const load = useCallback(() => {
    getMemberBills({ ...range, country: country || undefined, facility_id: gym || undefined, q: q.trim() || undefined })
      .then(setData).catch((e) => toast.error(errMsg(e, "Could not load the bills")));
  }, [range.from, range.to, country, gym, q]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);

  const countries = useMemo(() => [...new Set(gyms.map((g) => g.country).filter(Boolean))].sort(), [gyms]);
  const modes = useMemo(() => [...new Set((data?.rows || []).map((r) => r.payment_mode).filter(Boolean))].sort(), [data]);
  const rows = (data?.rows || []).filter((r) => (!mode || r.payment_mode === mode) && (!kind || KINDS[kind](r)) && (!email || EMAIL_STATES[email](r)));
  const totals = useMemo(() => {
    const t = {};
    rows.forEach((r) => {
      const x = t[r.currency_code] ||= { currency_code: r.currency_code, currency_symbol: r.currency_symbol, bills: 0, total: 0, tax: 0, not_emailed: 0 };
      x.bills += 1; x.total += Number(r.total) || 0; x.tax += Number(r.tax) || 0; x.not_emailed += r.emailed_to ? 0 : 1;
    });
    return Object.values(t).sort((a, b) => a.currency_code.localeCompare(b.currency_code));
  }, [rows]);
  const filtered = period !== "month" || month !== headerMonth || country || gym || mode || kind || email || q;
  const clear = () => { setPeriod("month"); setMonth(headerMonth); setFrom(""); setTo(""); setCountry(""); setGym(""); setMode(""); setKind(""); setEmail(""); setQ(""); };
  const sort = useSort(rows, COLS, "issued_on");

  const resend = async (r) => {
    const to = window.prompt(`Email bill ${r.number} to:`, r.email || "");
    if (to === null) return;
    setSending(r.id);
    try {
      const res = await emailMemberBill(r.id, to.trim());
      toast.success(`Bill ${r.number} emailed to ${res.to}`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "The email didn't go out"));
      load();
    } finally { setSending(null); }
  };

  const csv = () => downloadCsv(`member-bills-${range.from || "all"}${range.to ? `-to-${range.to}` : ""}.csv`,
    ["Bill no.", "Issued", "Gym", "Member", "Phone", "Email", "For", "Payment", "Mode", "Currency", "Amount", "Tax", "Balance left", "Emailed to", "Email problem"],
    rows.map((r) => [r.number, r.issued_on, r.gym, r.member, r.phone, r.email, r.description, r.label, r.payment_mode, r.currency_code,
      r.total, r.tax, r.balance, r.emailed_to, r.email_error]));

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  return (
    <div className="space-y-4" data-testid="accounts-bills">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {totals.length === 0 && <Stat label="Bills" value="0" />}
        {totals.map((t) => (
          <Stat key={t.currency_code} label={`Bills · ${t.currency_code}`} value={money(t, t.total)}
            sub={`${t.bills} bill${t.bills === 1 ? "" : "s"} · tax ${money(t, t.tax)}${t.not_emailed ? ` · ${t.not_emailed} not emailed` : ""}`}
            tone={t.not_emailed ? "bad" : undefined} />
        ))}
      </div>
      <div className="card-surface p-3 flex flex-wrap items-center gap-2" data-testid="bills-filters">
        <select value={period} onChange={(e) => setPeriod(e.target.value)} className={SELECT} data-testid="bills-period">
          <option value="month">Month</option>
          <option value="last_month">Last month</option>
          <option value="fy">This financial year</option>
          <option value="range">Date range</option>
          <option value="all">All time</option>
        </select>
        {period === "month" && <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className={SELECT} data-testid="bills-month" />}
        {period === "range" && (<>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={SELECT} aria-label="From" data-testid="bills-from" />
          <span className="text-xs text-zinc-500">to</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={SELECT} aria-label="To" data-testid="bills-to" />
        </>)}
        <select value={country} onChange={(e) => { setCountry(e.target.value); setGym(""); }} className={SELECT} data-testid="bills-country">
          <option value="">All countries</option>
          {countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={gym} onChange={(e) => setGym(e.target.value)} className={SELECT} data-testid="bills-gym">
          <option value="">All gyms</option>
          {gyms.filter((g) => !country || g.country === country).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <select value={mode} onChange={(e) => setMode(e.target.value)} className={SELECT} data-testid="bills-mode">
          <option value="">Any payment mode</option>
          {modes.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} className={SELECT} data-testid="bills-kind">
          <option value="">Any bill</option>
          <option value="full">Paid in full</option>
          <option value="part">Part payment</option>
          <option value="final">Final payment</option>
          <option value="old">Whole package (before 16 Oct)</option>
        </select>
        <select value={email} onChange={(e) => setEmail(e.target.value)} className={SELECT} data-testid="bills-email">
          <option value="">Any email status</option>
          <option value="emailed">Emailed</option>
          <option value="problem">Not emailed (any reason)</option>
          <option value="failed">Email didn't go out</option>
          <option value="not_sent">Never emailed</option>
        </select>
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Bill no., member, phone, email"
            className="bg-[#0a0a0a] border border-[#262626] rounded-md h-8 pl-8 pr-2 text-sm text-zinc-200 w-60" data-testid="bills-search" />
        </div>
        {filtered && <button type="button" onClick={clear} className="text-xs text-blue-300 hover:text-blue-200" data-testid="bills-clear">Clear filters</button>}
      </div>
      <div className="card-surface overflow-x-auto">
        <div className="px-4 py-3 flex flex-wrap items-center gap-3 justify-between">
          <div className="text-sm text-zinc-300">{rows.length} bill{rows.length === 1 ? "" : "s"}
            <span className="text-zinc-500"> · {range.from || range.to ? `${range.from || "start"} to ${range.to || "today"}` : "all time"}</span></div>
          <CsvButton onClick={csv} disabled={!rows.length} testid="bills-csv" />
        </div>
        {rows.length === 0 ? <Empty>No bills match these filters.</Empty> : (
          <table className="w-full text-sm min-w-[980px]">
            <thead><tr className={TH}>
              <SortTh sort={sort} k="issued_on">Issued</SortTh>
              <SortTh sort={sort} k="number">Bill no.</SortTh>
              <SortTh sort={sort} k="gym">Gym</SortTh>
              <SortTh sort={sort} k="member">Member</SortTh>
              <th className="px-3 py-2 font-medium">For</th>
              <SortTh sort={sort} k="total" right>Amount</SortTh>
              <th className="px-3 py-2 font-medium">Emailed</th>
              <th className="px-3 py-2" />
            </tr></thead>
            <tbody>
              {sort.sorted.map((r) => (
                <tr key={r.id} className={TR} data-testid={`bill-row-${r.id}`}>
                  <td className="px-3 py-2 text-zinc-400 tabular-nums whitespace-nowrap">{r.issued_on}</td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-200 whitespace-nowrap">{r.number}</td>
                  <td className="px-3 py-2 text-zinc-400">{r.gym}</td>
                  <td className="px-3 py-2 text-zinc-200">{r.member}<div className="text-[11px] text-zinc-500">{r.phone}</div></td>
                  <td className="px-3 py-2 text-xs text-zinc-400">{r.description}
                    {r.label && <div className="text-zinc-500">{r.label}{Number(r.balance) > 0 ? ` · ${money(r, r.balance)} left` : ""}</div>}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r, r.total, true)}
                    <div className="text-[11px] text-zinc-500">{r.payment_mode}{Number(r.tax) ? ` · tax ${money(r, r.tax, true)}` : ""}</div></td>
                  <td className="px-3 py-2 text-xs">
                    {r.emailed_to
                      ? <span className="text-emerald-300" title={r.emailed_at}>{r.emailed_to}</span>
                      : <span className="text-rose-300" title={r.email_error || ""}>{r.email_error ? "Didn't go out" : "Not emailed"}
                          {r.email_error && <div className="text-[11px] text-zinc-500 max-w-[220px] truncate">{r.email_error}</div>}</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <a href={accountsBillPdfUrl(r.id)} target="_blank" rel="noreferrer"
                      className="text-blue-300 hover:text-blue-200 text-xs inline-flex items-center gap-1 mr-3" data-testid="bill-pdf">
                      <FileText className="h-3.5 w-3.5" /> PDF</a>
                    <button onClick={() => resend(r)} disabled={sending === r.id}
                      className="text-zinc-400 hover:text-zinc-200 text-xs inline-flex items-center gap-1" data-testid="bill-email">
                      {sending === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                      {r.emailed_to ? "Email again" : "Email"}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
